import { styled } from 'styled-components';
import moment from 'moment';
import { useEffect, useState, useRef } from 'react';

import { Tournament } from '@src/lib/types';
import { GameState } from '@src/state/slices/types';

import SmallButton from '@src/components/styled/SmallButton';

import TournamentGameRow from './TournamentGameRow';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;

  border: 2pt solid gray;
  padding: 5pt;
  margin-bottom: 5pt;

  // color hyperlink blue
  color: #0000ff;

  //background color light gray
  background-color: #cfcfcf;

  overflow-x: clip;
  
  -khtml-user-select: none;
  -o-user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;

  @media only screen and (max-width: 720px) {
    justify-content: center;
    align-items: center;
    align-text: center;
    text-align: center;

    padding: 5pt;
  }

  a {
    flex-grow: 1;
    display: flex;
    // margin-right: 10pt;
    font-size: 2rem;
  }

  div {
    // debug border
    // box-sizing: border-box;
    // -moz-box-sizing: border-box;
    // -webkit-box-sizing: border-box;
    // border: 1pt solid blue;

    @media only screen and (max-width: 1280px) {
      flex: 1;
      min-width: 100%;
      max-width: 100%;
      font-size: 1.5rem;
      margin-top: 5pt;
      text-align: center;
    }
  }

  button {
    @media only screen and (max-width: 720px) {
      margin-top: 5pt;
    }
  }
`;

const GameName = styled.div`
  font-size: 2rem;

  min-width: 27rem;
  max-width: 27rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: black;
`;

const Players = styled.div`
  font-size: 2rem;

  min-width: 15rem;
  max-width: 15rem;

  text-align: center;
`;

const Status = styled.div<{ color?: string }>`
  font-size: 2rem;

  min-width: 25rem;
  max-width: 25rem;
  color: ${(props) => props.color || 'blue'};
  text-align: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;

  min-width: 7rem;
  max-width: 7rem;

  button {
    margin-top: 5pt;
  }

  @media only screen and (max-width: 820px) {
    flex-direction: row;
    min-width: 7rem;
    max-width: 10rem;
  }
`;

const GameRowContainer = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;

  margin-top: 10pt;
  border: 2pt solid gray;
  padding: 5pt;

  width: 95%;

  overflow-y: scroll;

  // color hyperlink blue
  color: #0000ff;

  //background color windows 98 gray
  background-color: #c0c0c0;
`;

type Props = {
  name: string;
  tournament: Tournament;
  games: GameState['games'];
  playerId: string;
  joinTournament: (tournamentId: string) => void;
  leaveTournament: (tournamentId: string) => void;
  joinGame: (gameId: string) => void;
  watchGame: (gameId: string) => void;
  filterTournamentGames: boolean;
};

const calculateDuration = (eventTime: number) => {
  return moment.duration(
    Math.max(eventTime / 1000 - Math.floor(Date.now() / 1000), 0),
    'seconds',
  );
};

export default function TournamentRow(props: Props) {
  const [timeLeft, setTimeLeft] = useState(
    calculateDuration(props.tournament.startTime).asSeconds() || 0,
  );
  useEffect(() => {
    if (timeLeft <= 0) return;

    // Update every second
    const timerId = setInterval(() => {
      setTimeLeft((prevTimeLeft) => prevTimeLeft - 1);
    }, 1000);

    // Clean up the interval on unmount
    return () => clearInterval(timerId);
  }, [timeLeft]);
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const timeDisplay = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  // END TIMER

  // PLAYER INFO UTILITIES
  const getPlayerParticipant = () => {
    return props.tournament.participants.find(
      (player) => player.playerId === props.playerId,
    );
  };
  const playerParticipantRef = useRef(getPlayerParticipant());
  useEffect(() => {
    playerParticipantRef.current = getPlayerParticipant();
  }, [props.tournament.participants]);
  const playerWasEliminated = () => {
    if (!playerParticipantRef.current) return false;
    return playerParticipantRef.current.status === 'eliminated';
  };

  const getPlayersGameIsActive = () => {
    return Object.values(props.games)
      .filter(
        (game) =>
          game.type === 'tournament' &&
          game.tournamentId === props.tournament.tournamentId &&
          game.whitelist.includes(props.playerId),
      )
      .some((game) => !game.terminated && !game.gameEnded);
  };

  const getRemainingPlayerCount = () => {
    return props.tournament.participants.filter(
      (player) => player.status !== 'eliminated',
    ).length;
  };

  // GET STATUS TO SHOW IN TOURNAMENT ROW
  const getStatus = () => {
    let statusText: string = props.tournament.status;
    let secondStatus: string | null = null;
    let statusColor = 'blue';
    switch (props.tournament.status) {
      case 'waitingRoom':
        if (timeDisplay === '0:00') {
          statusText = 'tournament is starting';
          statusColor = 'green';
          break;
        }

        statusText = `starts in ${timeDisplay}`;
        break;
      case 'running':
        if (playerWasEliminated()) {
          statusText = 'you were eliminated';
          statusColor = 'red';
          break;
        }

        if (getPlayersGameIsActive()) {
          statusText = 'join game';
          statusColor = 'green';
        } else {
          if (playerParticipantRef.current) {
            statusText = 'wait for round to finish';
            statusColor = 'orange';
          } else {
            statusText = 'tournament is running';
            statusColor = 'orange';
          }
        }
        break;
      case 'finished': {
        statusText = 'Top Five Players';
        statusColor = 'blue';
        const topFive = props.tournament.topFive
          ?.map((player, i) => `${i + 1}-${player.playerName}`)
          .join(', ');
        secondStatus = topFive || '...';
        break;
      }
      case 'canceled':
        statusText = 'tournament was canceled';
        statusColor = 'blue';
        break;
    }

    return (
      <Status color={statusColor}>
        {statusText}
        {secondStatus && (
          <>
            <br />
          </>
        )}
        {secondStatus}
      </Status>
    );
  };

  return (
    <Container>
      <GameName>{props.name}</GameName>
      {getStatus()}
      <Players>
        players: {getRemainingPlayerCount()}/
        {props.tournament.participants.length}
      </Players>
      <ButtonContainer>
        {playerParticipantRef.current &&
          props.tournament.status === 'waitingRoom' && (
            <SmallButton
              onClick={() =>
                props.leaveTournament(props.tournament.tournamentId)
              }
              disabled={props.tournament.status !== 'waitingRoom'}
            >
              LEAVE
            </SmallButton>
          )}
        {!playerParticipantRef.current &&
          props.tournament.status === 'waitingRoom' && (
            <SmallButton
              onClick={() =>
                props.joinTournament(props.tournament.tournamentId)
              }
            >
              JOIN
            </SmallButton>
          )}
      </ButtonContainer>

      {/* LIST GAMES */}
      {props.tournament.status === 'running' && (
        <GameRowContainer>
          {Object.values(props.games)
            .filter(
              (game) =>
                game.type === 'tournament' &&
                game.tournamentId === props.tournament.tournamentId,
            )
            .filter((game) => !game.terminated && !game.gameEnded)
            .filter((game) => {
              if (props.filterTournamentGames) {
                return game.whitelist?.includes(props.playerId);
              } else {
                return true;
              }
            })
            .map((game) => (
              <TournamentGameRow
                key={game.gameId}
                name={game.name}
                gameId={game.gameId}
                numPlayers={game.players?.length || 0}
                maxPlayers={game.whitelist?.length || 10}
                isPublic={game.isPublic}
                gameStarted={game.gameStart}
                playerCanJoin={
                  game.whitelist === undefined ||
                  game.whitelist.includes(props.playerId)
                }
                joinGame={props.joinGame}
                watchGame={props.watchGame}
              />
            ))}
        </GameRowContainer>
      )}
    </Container>
  );
}
